import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import type { District, DistrictPhotoConfig } from '@citizen-walk/shared-types';
import { useState } from 'react';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';

/**
 * 地区別 投稿写真設定 panel (Task C)。
 *
 * master のみ編集可 (`canEdit`)、地区管理者には読取専用 (グレー表示) で表示する。
 * 旧 v1/v2 では投稿写真は固定パラメータ (1600px / 1.5MB / quality 0.85) で client 圧縮していたが、
 * 「地区ごとに高画質・大容量で保存したい / 元画像を無圧縮で残したい」要望に応えて master が地区単位で
 * 上書きできるようにする。
 *
 * 書込先: `districts/{id}.photoConfig` (`DistrictPhotoConfig`、setDoc merge)。
 * 消費側: user の `resolveDistrictByToken` → `useDistrict().photoConfig` → `preprocessPhoto` /
 * アップロード前サイズ検証。`maxUploadSizeMB` は storage.rules の全体上限 (30MB) 以下に保つこと。
 */

// 既定値 (user 側 photo.ts と一致させる。未設定地区はこの値で従来動作)。
const DEFAULT_MAX_WIDTH = 1600;
const DEFAULT_MAX_MB = 1.5;
const DEFAULT_QUALITY = 0.85;
const DEFAULT_MAX_UPLOAD_MB = 10;
/** storage.rules の global hard ceiling (MB)。maxUploadSizeMB はこれ以下であること。 */
export const STORAGE_CEILING_MB = 30;

export interface DistrictPhotoDraft {
  uploadOriginal: boolean;
  maxWidthOrHeight: number;
  maxSizeMB: number;
  quality: number;
  maxUploadSizeMB: number;
  photoDownloadMode: 'zip' | 'individual';
}

/** pure: District → draft (未設定は既定値)。 */
export function readPhotoConfigFromDistrict(d: District): DistrictPhotoDraft {
  const pc = d.photoConfig ?? {};
  return {
    uploadOriginal: pc.uploadOriginal === true,
    maxWidthOrHeight:
      typeof pc.maxWidthOrHeight === 'number' && pc.maxWidthOrHeight > 0
        ? pc.maxWidthOrHeight
        : DEFAULT_MAX_WIDTH,
    maxSizeMB: typeof pc.maxSizeMB === 'number' && pc.maxSizeMB > 0 ? pc.maxSizeMB : DEFAULT_MAX_MB,
    quality:
      typeof pc.quality === 'number' && pc.quality > 0 && pc.quality <= 1
        ? pc.quality
        : DEFAULT_QUALITY,
    maxUploadSizeMB:
      typeof pc.maxUploadSizeMB === 'number' && pc.maxUploadSizeMB > 0
        ? pc.maxUploadSizeMB
        : DEFAULT_MAX_UPLOAD_MB,
    photoDownloadMode: pc.photoDownloadMode === 'individual' ? 'individual' : 'zip',
  };
}

function clamp(v: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(v)) return fallback;
  return Math.min(max, Math.max(min, v));
}

/** pure: draft → 正規化済 photoConfig payload (範囲 clamp)。 */
export function buildPhotoConfigPayload(draft: DistrictPhotoDraft): DistrictPhotoConfig {
  return {
    uploadOriginal: !!draft.uploadOriginal,
    maxWidthOrHeight: Math.round(clamp(draft.maxWidthOrHeight, 320, 8192, DEFAULT_MAX_WIDTH)),
    maxSizeMB: clamp(draft.maxSizeMB, 0.1, STORAGE_CEILING_MB, DEFAULT_MAX_MB),
    quality: clamp(draft.quality, 0.1, 1, DEFAULT_QUALITY),
    maxUploadSizeMB: clamp(draft.maxUploadSizeMB, 1, STORAGE_CEILING_MB, DEFAULT_MAX_UPLOAD_MB),
    photoDownloadMode: draft.photoDownloadMode === 'individual' ? 'individual' : 'zip',
  };
}

export interface DistrictPhotoSettingsPanelProps {
  district: District;
  uid: string;
  canEdit: boolean;
}

export function DistrictPhotoSettingsPanel({
  district,
  uid,
  canEdit,
}: DistrictPhotoSettingsPanelProps) {
  const { toast } = useToast();
  const [draft, setDraft] = useState<DistrictPhotoDraft>(() =>
    readPhotoConfigFromDistrict(district),
  );
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!canEdit) return;
    setSaving(true);
    try {
      const photoConfig = buildPhotoConfigPayload(draft);
      const ref = doc(db, 'districts', district.id);
      await setDoc(
        ref,
        { photoConfig, updatedAt: serverTimestamp(), updatedBy: uid },
        { merge: true },
      );
      // 正規化後の値を draft に反映 (clamp 結果を UI に戻す)。
      setDraft({
        uploadOriginal: photoConfig.uploadOriginal ?? false,
        maxWidthOrHeight: photoConfig.maxWidthOrHeight ?? DEFAULT_MAX_WIDTH,
        maxSizeMB: photoConfig.maxSizeMB ?? DEFAULT_MAX_MB,
        quality: photoConfig.quality ?? DEFAULT_QUALITY,
        maxUploadSizeMB: photoConfig.maxUploadSizeMB ?? DEFAULT_MAX_UPLOAD_MB,
        photoDownloadMode: photoConfig.photoDownloadMode === 'individual' ? 'individual' : 'zip',
      });
      toast('success', '投稿画像設定を保存しました');
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  const compressionDisabled = !canEdit || saving || draft.uploadOriginal;
  const numberInputClass =
    'min-h-touch px-3 border border-slate-300 rounded text-sm bg-white disabled:bg-slate-100 disabled:text-slate-400';

  return (
    <Card padding="lg" className="border-primary-200" data-testid="district-photo-settings-panel">
      <h3 className="text-base font-bold text-slate-900 mb-1">📷 投稿画像の設定 (Task C)</h3>
      <p className="text-xs text-slate-500 mb-3">
        地区ごとに投稿写真の画質・最大サイズを変更できます。マスター管理者のみ変更可能です。
      </p>

      {!canEdit && (
        <p className="text-xs text-warning-700 mb-3" data-testid="photo-readonly-note">
          マスター管理者のみ変更できます (現在は読み取り専用)。
        </p>
      )}

      <div className="grid grid-cols-1 gap-3">
        <label className="grid grid-cols-1 gap-1" data-testid="photo-download-mode-wrap">
          <span className="text-sm font-medium text-slate-800">
            参加者の「自分の写真ダウンロード」方式
          </span>
          <select
            value={draft.photoDownloadMode}
            onChange={(e) =>
              setDraft({
                ...draft,
                photoDownloadMode: e.target.value === 'individual' ? 'individual' : 'zip',
              })
            }
            disabled={!canEdit || saving}
            className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white disabled:bg-slate-100 disabled:text-slate-400"
            data-testid="photo-download-mode"
          >
            <option value="zip">まとめて ZIP でダウンロード（既定）</option>
            <option value="individual">1 枚ずつ個別にダウンロード</option>
          </select>
          <span className="text-[11px] text-slate-400">
            ZIP を解凍できないスマートフォン参加者が多い地区では「1 枚ずつ個別」を選ぶと、
            参加者は写真ごとに「保存」をタップして 1 枚ずつ端末に保存できます。
          </span>
        </label>

        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={draft.uploadOriginal}
            onChange={(e) => setDraft({ ...draft, uploadOriginal: e.target.checked })}
            disabled={!canEdit || saving}
            className="h-4 w-4 mt-0.5"
            data-testid="photo-upload-original"
          />
          <span className="text-sm">
            元画像を圧縮せずそのままアップロードする
            <span className="block text-[11px] text-slate-400">
              ON のとき下記の解像度・品質・目標サイズは無視されます。EXIF (位置情報)
              が残る場合があり、 一部端末の HEIC 形式は表示できないことがあります。
            </span>
          </span>
        </label>

        <label className="grid grid-cols-1 gap-1">
          <span className="text-xs text-slate-500">圧縮後の最大解像度 (長辺 px、既定 1600)</span>
          <input
            type="number"
            min={320}
            max={8192}
            step={100}
            value={draft.maxWidthOrHeight}
            onChange={(e) => setDraft({ ...draft, maxWidthOrHeight: Number(e.target.value) })}
            disabled={compressionDisabled}
            className={numberInputClass}
            data-testid="photo-max-width"
          />
        </label>

        <label className="grid grid-cols-1 gap-1">
          <span className="text-xs text-slate-500">
            圧縮後の目標ファイルサイズ (MB、既定 1.5。大きいほど高画質)
          </span>
          <input
            type="number"
            min={0.1}
            max={STORAGE_CEILING_MB}
            step={0.5}
            value={draft.maxSizeMB}
            onChange={(e) => setDraft({ ...draft, maxSizeMB: Number(e.target.value) })}
            disabled={compressionDisabled}
            className={numberInputClass}
            data-testid="photo-max-size"
          />
        </label>

        <label className="grid grid-cols-1 gap-1">
          <span className="text-xs text-slate-500">
            圧縮品質 (0〜1、既定 0.85。1 に近いほど高画質=低圧縮)
          </span>
          <input
            type="number"
            min={0.1}
            max={1}
            step={0.05}
            value={draft.quality}
            onChange={(e) => setDraft({ ...draft, quality: Number(e.target.value) })}
            disabled={compressionDisabled}
            className={numberInputClass}
            data-testid="photo-quality"
          />
        </label>

        <label className="grid grid-cols-1 gap-1">
          <span className="text-xs text-slate-500">
            アップロード許可する最大ファイルサイズ (MB、既定 10。上限 {STORAGE_CEILING_MB})
          </span>
          <input
            type="number"
            min={1}
            max={STORAGE_CEILING_MB}
            step={1}
            value={draft.maxUploadSizeMB}
            onChange={(e) => setDraft({ ...draft, maxUploadSizeMB: Number(e.target.value) })}
            disabled={!canEdit || saving}
            className={numberInputClass}
            data-testid="photo-max-upload"
          />
          <span className="text-[11px] text-slate-400">
            これを超える画像は投稿前に拒否されます。元画像・圧縮後どちらにも適用されます。
          </span>
        </label>
      </div>

      <div className="flex justify-end mt-3">
        <Button
          onClick={() => void handleSave()}
          disabled={!canEdit || saving}
          data-testid="photo-save"
        >
          {saving ? '保存中…' : '保存'}
        </Button>
      </div>
    </Card>
  );
}
